import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useBedarfeKanban, useBedarf } from '../../api/bedarfe';
import { useGenerateContract } from '../../api/bewerber';
import type { Bewerber } from '../../types/bewerber';
import { BedarfStatus, calculateTotalHours, formatHours } from '../../types/bedarf';

interface ArbeitsvertragModalProps {
  isOpen: boolean;
  onClose: () => void;
  bewerber: Bewerber;
  onSuccess?: () => void;
}

interface FormData {
  bedarfId: string;
  startdatum: string;
}

export function ArbeitsvertragModal({
  isOpen,
  onClose,
  bewerber,
  onSuccess,
}: ArbeitsvertragModalProps) {
  const [error, setError] = useState<string | null>(null);
  const [selectedBedarfId, setSelectedBedarfId] = useState<string>('');

  const generateMutation = useGenerateContract();
  const { data: bedarfeData, isLoading: isLoadingBedarfe } = useBedarfeKanban();
  const { data: bedarfData } = useBedarf(selectedBedarfId);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      bedarfId: '',
      startdatum: '',
    },
  });

  const watchBedarfId = watch('bedarfId');

  // Filter Bedarfe with status OFFEN, KENNENLERNGESPRAECH, TERMINIERT or
  // KOSTENTRAEGER_GO
  const bedarfOptions = useMemo(() => {
    if (!bedarfeData?.data) return [];

    return bedarfeData.data.filter(
      (bedarf) =>
        bedarf.status === BedarfStatus.OFFEN ||
        bedarf.status === BedarfStatus.KENNENLERNGESPRAECH ||
        bedarf.status === BedarfStatus.TERMINIERT ||
        bedarf.status === BedarfStatus.KOSTENTRAEGER_GO
    );
  }, [bedarfeData]);

  // Calculate preview values when Bedarf is selected
  const previewData = useMemo(() => {
    const bedarf = bedarfData?.data;
    if (!bedarf || !bewerber.angebotenerStundensatz) {
      return null;
    }

    const hoursPerWeek = calculateTotalHours(bedarf);
    if (hoursPerWeek === 0) {
      return null;
    }
    // Round overtime (23.6%) to nearest 0.5
    const rawOvertime = hoursPerWeek * 0.236;
    const wochenueberstunden = Math.round(rawOvertime * 2) / 2;
    // Regular hours = total - rounded overtime (ensures sum equals total)
    const wochenstunden = hoursPerWeek - wochenueberstunden;
    const stundensatz = Number(bewerber.angebotenerStundensatz);
    const bruttogehalt = stundensatz * wochenstunden * 4.33;

    return {
      wochenstunden: wochenstunden.toFixed(1),
      wochenueberstunden: wochenueberstunden.toFixed(1),
      bruttogehalt: bruttogehalt.toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      hoursPerWeek: hoursPerWeek.toFixed(1),
    };
  }, [bedarfData, bewerber.angebotenerStundensatz]);

  // Validation warnings
  const warnings = useMemo(() => {
    const list: string[] = [];
    if (!bewerber.gender || bewerber.gender === 'divers') {
      list.push('Geschlecht muss auf männlich oder weiblich gesetzt sein');
    }
    if (!bewerber.angebotenerStundensatz) {
      list.push('Kein angebotener Stundensatz hinterlegt');
    }
    if (!bewerber.street) {
      list.push('Keine Straße hinterlegt');
    }
    if (!bewerber.postalCode) {
      list.push('Keine PLZ hinterlegt');
    }
    if (!bewerber.city) {
      list.push('Kein Wohnort hinterlegt');
    }
    return list;
  }, [bewerber]);

  const handleBedarfChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const bedarfId = e.target.value;
    setSelectedBedarfId(bedarfId);
  };

  const handleFormSubmit = async (data: FormData) => {
    setError(null);
    try {
      await generateMutation.mutateAsync({
        bewerberId: bewerber.id,
        data: {
          bedarfId: data.bedarfId,
          startdatum: data.startdatum,
        },
      });
      toast.success('Arbeitsvertrag erfolgreich generiert');
      reset();
      setSelectedBedarfId('');
      onSuccess?.();
      onClose();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Fehler beim Generieren des Arbeitsvertrags';
      setError(errorMessage);
      toast.error('Fehler beim Generieren des Arbeitsvertrags');
      console.error('Failed to generate contract:', err);
    }
  };

  const handleClose = () => {
    reset();
    setSelectedBedarfId('');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const hasValidGender = bewerber.gender && bewerber.gender !== 'divers';
  const bedarfHasHours = bedarfData?.data && calculateTotalHours(bedarfData.data) > 0;
  const canGenerate = watchBedarfId && bedarfHasHours && bewerber.angebotenerStundensatz && hasValidGender;

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-teal-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Arbeitsvertrag generieren
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Generieren Sie einen Arbeitsvertrag für <span className="font-medium">{bewerber.firstName} {bewerber.lastName}</span>.
                  </p>

                  {/* Warnings */}
                  {warnings.length > 0 && (
                    <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                      <p className="text-sm font-medium text-yellow-800 mb-1">Fehlende Daten:</p>
                      <ul className="text-sm text-yellow-700 list-disc list-inside">
                        {warnings.map((warning, index) => (
                          <li key={index}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {error && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}

                  <div className="mt-4 space-y-4">
                    {/* Bedarf Selection */}
                    <div>
                      <label htmlFor="bedarfId" className="block text-sm font-medium text-gray-700">
                        Kind auswählen *
                      </label>
                      <select
                        id="bedarfId"
                        {...register('bedarfId', { required: 'Bitte wählen Sie ein Kind aus' })}
                        onChange={(e) => {
                          register('bedarfId').onChange(e);
                          handleBedarfChange(e);
                        }}
                        disabled={isLoadingBedarfe}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
                      >
                        <option value="">
                          {isLoadingBedarfe ? 'Lade...' : '-- Kind auswählen --'}
                        </option>
                        {bedarfOptions.map((bedarf) => (
                          <option key={bedarf.id} value={bedarf.id}>
                            {bedarf.childFirstName} {bedarf.childLastName} - {bedarf.schoolName || 'Ohne Schule'}
                          </option>
                        ))}
                      </select>
                      {errors.bedarfId && (
                        <p className="mt-1 text-sm text-red-600">{errors.bedarfId.message}</p>
                      )}
                      {bedarfOptions.length === 0 && !isLoadingBedarfe && (
                        <p className="mt-1 text-sm text-yellow-600">
                          Keine offenen Bedarfe gefunden.
                        </p>
                      )}
                    </div>

                    {/* Startdatum */}
                    <div>
                      <label htmlFor="startdatum" className="block text-sm font-medium text-gray-700">
                        Startdatum *
                      </label>
                      <input
                        type="date"
                        id="startdatum"
                        {...register('startdatum', { required: 'Startdatum ist erforderlich' })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                      {errors.startdatum && (
                        <p className="mt-1 text-sm text-red-600">{errors.startdatum.message}</p>
                      )}
                    </div>

                    {/* Preview Section */}
                    {selectedBedarfId && (
                      <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                        <p className="text-sm font-medium text-gray-700 mb-2">Vertragsdetails (berechnet):</p>
                        {previewData ? (
                          <div className="space-y-1 text-sm text-gray-600">
                            <p>Stunden Bedarf: <span className="font-medium">{formatHours(Number(previewData.hoursPerWeek))}</span></p>
                            <p>Wochenstunden: <span className="font-medium">{previewData.wochenstunden} Std.</span></p>
                            <p>Wochenüberstunden (23,6%): <span className="font-medium">{previewData.wochenueberstunden} Std.</span></p>
                            <p>Bruttogehalt: <span className="font-medium">{previewData.bruttogehalt} €</span></p>
                            <p className="text-xs text-gray-500 mt-2">
                              Berechnung: {bewerber.angebotenerStundensatz} € × {previewData.wochenstunden} Std. × 4,33
                            </p>
                          </div>
                        ) : (
                          <p className="text-sm text-yellow-600">
                            {!bedarfHasHours
                              ? 'Bedarf hat keine Wochenstunden hinterlegt'
                              : 'Bewerber hat keinen angebotenen Stundensatz'}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={generateMutation.isPending || !canGenerate}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-teal-600 text-base font-medium text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                {generateMutation.isPending ? 'Wird generiert...' : 'Herunterladen'}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}
